import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CalendarDays, ClipboardList, Plane, MoreHorizontal, Receipt, Settings,
  Users, GraduationCap, Ship, Inbox, Upload, FileCheck, LogOut,
  Moon, Sun, MessageCircleQuestion, Home,
} from "lucide-react";
import theme, { getThemeMode, toggleThemeMode } from "../theme";
import seapaLogo from "../assets/SeapaLogo.png";
import { buildNav } from "../navigation/navConfig";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";
import { computeSyncSummary } from "../lib/syncSummary";
import { localToday } from "../lib/dates";
import { fetchAllPages, loadAuditMonthsStrict, loadPilotCreated } from "../lib/auditDue";
import { needsAttentionCount } from "../lib/committeeStatus";
import { isLicensedDeputy } from "../lib/traineeKind";
import { ridesWithoutTr, rideRecords, lastDayFilter, TR_GO_LIVE } from "../lib/ridesWithoutTr";
import { unseenCount, SEEN_EVENT } from "../data/announcements";
import { emailable } from "../../supabase/functions/_shared/trDigest.js";

// One nav per person ("one person, one home", Jill 2026-07-02):
//  - desktop (>=820px): top bar — logo, icon tabs, More menu, account button
//  - phone / iPad (<820px): slim top bar + app-style bottom tab bar (first 4
//    tabs + More sheet). No dashboard switcher, no hamburger.
// Red attention dots replace the old dashboard "Updated" badges: dispatch
// changes dot My Schedule, reservation changes dot Travel, open board tickets /
// ride requests dot their items in More.

const ICONS = {
  schedule: CalendarDays,
  workspace: ClipboardList,
  travel: Plane,
  expenses: Receipt,
  paperwork: FileCheck,
  admin: Settings,
  opportunities: GraduationCap,
  roster: Users,
  vessels: Ship,
  queue: Inbox,
  upload: Upload,
  ask: MessageCircleQuestion,
  home: Home,
};

function useIsNarrow(breakpoint = 820) {
  const [narrow, setNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return narrow;
}

// Live attention state — same signals the dashboard cards used to show.
function useAttention(user, boardPosition, committeePositions) {
  const [dots, setDots] = useState({ travel: false, dispatch: false, board: 0, chair: 0, chairReview: 0, grading: 0, training: false, mentor: 0, cargo: 0, paperwork: 0, committee: 0 });
  const [navFlags, setNavFlags] = useState({ isTrainingPilot: false, hasTrainingTrack: false, isMentor: false });
  const { pathname } = useLocation();
  // Recount when the pilot comes back to the app (at most every 5 minutes)
  // and every 10 minutes while it stays open.
  // The dots used to be counted only at sign-in, so a record sent to a pilot
  // with the app open never lit anything until a reload (Codex on #529).
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    let last = Date.now();
    const onShow = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 5 * 60 * 1000) return;
      last = Date.now();
      setRefreshTick((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onShow);
    // and every 10 minutes while the app stays open in front
    const iv = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      last = Date.now();
      setRefreshTick((n) => n + 1);
    }, 10 * 60 * 1000);
    // a page that changes what the dots count (Committee Review save) asks for a recount
    const onAsk = () => { last = Date.now(); setRefreshTick((n) => n + 1); };
    window.addEventListener("seapa:attention-refresh", onAsk);
    return () => { document.removeEventListener("visibilitychange", onShow); window.removeEventListener("seapa:attention-refresh", onAsk); clearInterval(iv); };
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const next = { travel: false, dispatch: false, board: 0, chair: 0, chairReview: 0, grading: 0, training: false, mentor: 0, cargo: 0, paperwork: 0, committee: 0 };
        const { data: meRec } = await supabase.from("pilots_info")
          .select("id, first_name, last_name, trainee_phase, trainee_category, deputy_license_level, is_training_pilot, training_pilot, board_position, travel_seen_at, dispatch_seen_at")
          .eq("auth_user_id", user.id).maybeSingle();
        const pos = boardPosition || meRec?.board_position;
        let menteeIds = [];
        if (meRec) {
          const { data: links } = await supabase.from("trainee_mentors")
            .select("trainee_id").eq("mentor_pilot_id", meRec.id);
          menteeIds = (links || []).map((l) => l.trainee_id);
        }
        // Any licensed pilot can be named on an OBSERVATION trip (Jill
        // 2026-09-29), so the grading queue shows for anyone with a record
        // waiting on them, not only roster training pilots.
        let myGrading = 0;
        if (meRec) {
          // Paper-graded records are excluded: they are waiting on the mentor or
          // Deputy Organizer, not on the pilot who already signed the paper
          // (Jill 2026-07-27).
          const { count } = await supabase.from("tr_records")
            .select("id", { count: "exact", head: true })
            .eq("training_pilot_id", meRec.id).eq("status", "submitted").is("pdf_path", null);
          myGrading = count || 0;
        }
        if (meRec && !cancelled) {
          setNavFlags({
            isTrainingPilot: !!(meRec.is_training_pilot || meRec.training_pilot) || myGrading > 0,
            hasTrainingTrack: !!(meRec.trainee_category || meRec.deputy_license_level),
            isMentor: menteeIds.length > 0,
          });
        }
        if (menteeIds.length) {
          const { count: mCount } = await supabase.from("tr_records")
            .select("id", { count: "exact", head: true })
            .in("trainee_id", menteeIds).eq("status", "graded");
          // Paper records waiting for me to type the grades in. This was MISSING
          // (Jill 2026-07-27): a trainee could photograph a signed record, send
          // it, and it sat until the mentor happened to open the page — no badge,
          // no email. Silent miss, so it is counted now.
          const { count: paperCount } = await supabase.from("tr_records")
            .select("id", { count: "exact", head: true })
            .in("trainee_id", menteeIds).eq("status", "submitted").not("pdf_path", "is", null);
          // + phase-advancement requests awaiting the chair — the mentor
          // should see them while a comment can still help
          const { count: phCount } = await supabase.from("trainee_phase_requests")
            .select("id", { count: "exact", head: true })
            .in("trainee_id", menteeIds).eq("status", "requested");
          next.mentor = (mCount || 0) + (paperCount || 0) + (phCount || 0);
        }
        // Deputy Organizer: deputies have neither mentor nor auditor, so their
        // paper transfers and post-grading reviews land here. Same badge as the
        // mentor list, since both use the /mentor-review page.
        if ((committeePositions || []).includes("deputy_organizer")) {
          const { data: deps } = await supabase.from("pilots_info")
            .select("id").not("deputy_license_level", "is", null); // one rule: a license level
          const depIds = (deps || []).map((d) => d.id);
          if (depIds.length) {
            const { count: dTransfer } = await supabase.from("tr_records")
              .select("id", { count: "exact", head: true })
              .in("trainee_id", depIds).eq("status", "submitted").not("pdf_path", "is", null);
            const { count: dReview } = await supabase.from("tr_records")
              .select("id", { count: "exact", head: true })
              .in("trainee_id", depIds).eq("status", "graded");
            next.mentor += (dTransfer || 0) + (dReview || 0);
          }
        }
        next.grading = myGrading;
        // Committee Review: an Auditor's own assigned trainees who are overdue or
        // due within 14 days; the Deputy Organizer's licensed deputies, same rule.
        // The Trainee Organizer and the Chair get no count here. Best-effort:
        // any failure leaves the count at 0 (no dot, no error).
        try {
          const cp = committeePositions || [];
          const wantAudit = !!meRec && cp.includes("auditor");
          const wantDeputy = cp.includes("deputy_organizer");
          if (wantAudit || wantDeputy) {
            let people = [];
            if (wantAudit) {
              const { data: mine, error: e1 } = await supabase.from("trainee_auditors").select("trainee_id").eq("auditor_pilot_id", meRec.id);
              const ids = (mine || []).map((a) => a.trainee_id);
              if (e1) throw e1;
              if (ids.length) {
                const { data: ppl, error: e2 } = await supabase.from("pilots_info")
                  .select("id, trainee_category, trainee_phase, deputy_license_level, is_active").in("id", ids);
                if (e2) throw e2;
                people.push(...(ppl || []).filter((t) => t.is_active !== false && !isLicensedDeputy(t)
                  && (t.trainee_category || t.trainee_phase))); // same roster test as Committee Review
              }
            }
            if (wantDeputy) {
              const { data: deps, error: e3 } = await supabase.from("pilots_info")
                .select("id, deputy_license_level, is_active").not("deputy_license_level", "is", null);
              if (e3) throw e3;
              people.push(...(deps || []).filter((t) => t.is_active !== false));
            }
            if (people.length) {
              const ids = people.map((t) => t.id);
              const [vr, rr, months, created] = await Promise.all([
                fetchAllPages(() => supabase.from("trainee_verifications").select("id, trainee_id, kind, created_at, verified_by_position").in("trainee_id", ids).order("created_at").order("id")),
                fetchAllPages(() => supabase.from("tr_records").select("id, trainee_id, record_date").in("trainee_id", ids).order("id")),
                loadAuditMonthsStrict(supabase),
                loadPilotCreated(supabase),
              ]);
              if (vr.error || rr.error || months == null) throw vr.error || rr.error || new Error("cadence unreadable");
              next.committee = needsAttentionCount(people, { verifications: vr.data, records: rr.data, pilotCreated: created, months });
            }
          }
        } catch { next.committee = 0; }
        if (meRec) {
          const { count: retCount } = await supabase.from("tr_records")
            .select("id", { count: "exact", head: true })
            .eq("trainee_id", meRec.id).in("status", ["graded", "reviewed"]).eq("seen_by_trainee", false);
          next.training = (retCount || 0) > 0;
          // a ride that ended with no Training Record also lights the dot (B6)
          if (!next.training) {
            const today = localToday();
            const { data: endedRides } = await supabase.from("training_trips")
              .select("id, trainee_id, status, start_date, end_date, completion_note")
              .eq("trainee_id", meRec.id).eq("status", "planned")
              .or(lastDayFilter({ gte: TR_GO_LIVE, lt: today }));
            if (endedRides?.length) {
              const { data: myRecs } = await supabase.from("tr_records")
                .select("trainee_id, record_date, training_trip_id, cargo_event_id")
                .eq("trainee_id", meRec.id)
                .or(`record_date.gte.${TR_GO_LIVE},training_trip_id.in.(${endedRides.map((r) => r.id).join(",")})`);
              next.training = ridesWithoutTr(endedRides, myRecs || [], { today }).length > 0;
            }
          }
          // an approved ride still ahead, waiting on "Create Training Records" (Jill
          // 2026-10-23: the trainee hears of an approval by email AND here)
          if (!next.training) {
            const today = localToday();
            const { data: aheadRides } = await supabase.from("training_trips")
              .select("id, trainee_id, start_date, end_date, completion_note")
              .eq("trainee_id", meRec.id).eq("status", "planned")
              .or(lastDayFilter({ gte: today })); // a one-day ride has no end date
            const asking = (aheadRides || []).filter((t) => !(t.completion_note || "").trim());
            if (asking.length) {
              const { data: myRecs } = await supabase.from("tr_records")
                .select("trainee_id, record_date, training_trip_id, cargo_event_id")
                .eq("trainee_id", meRec.id)
                .or(`record_date.gte.${asking.map((t) => t.start_date).sort()[0]},training_trip_id.in.(${asking.map((t) => t.id).join(",")})`);
              next.training = asking.some((t) => !rideRecords(t, myRecs || []).length);
            }
          }
        }

        // cargo: open legs still waiting on MY request-or-pass. Maneuvering
        // members must answer every ship (Jill 2026-07-18: request the work
        // or pass); the dot is data-driven — it clears when they answer, not
        // when they visit the page.
        if (meRec && (meRec.trainee_phase || meRec.deputy_license_level)) {
          const { data: mem } = await supabase.from("cargo_pool_members")
            .select("id").eq("pilot_id", meRec.id).eq("category", "maneuvering").eq("active", true).maybeSingle();
          if (mem) {
            const { data: openEvs } = await supabase.from("cargo_events").select("id").eq("status", "open");
            const evIds = (openEvs || []).map((e) => e.id);
            if (evIds.length) {
              const [{ data: legRows }, { data: myRows }] = await Promise.all([
                supabase.from("cargo_event_legs").select("id, event_id").in("event_id", evIds),
                supabase.from("cargo_claims").select("leg_id, role").eq("member_id", mem.id).in("event_id", evIds),
              ]);
              const answered = new Set((myRows || []).filter((c) => c.role === "work").map((c) => c.leg_id));
              next.cargo = (legRows || []).filter((l) => !answered.has(l.id)).length;
            }
          }
        }

        if (meRec?.last_name) {
          const LAST = meRec.last_name.toUpperCase();
          const MY_INITIAL = (meRec.first_name || "").trim().charAt(0).toUpperCase();
          // travel labels: "LASTNAME" or "LASTNAME, F" — when the office adds
          // the initial (same-surname pilots) it must be MINE, not my namesake's
          const travelLabelMine = (label) => {
            const parts = String(label || "").toUpperCase().split(",");
            if (parts[0].trim() !== LAST) return false;
            // a trainee only owns an explicitly-initialed label — a bare
            // surname on the office sheets always means the full pilot
            if (parts.length < 2) return !meRec.trainee_phase;
            return parts[1].trim().charAt(0) === MY_INITIAL;
          };
          // dispatch labels: strip GPM/tonnage, surname first, and any explicit
          // single-letter initial that isn't mine (or a J/K home-port letter)
          // means the change is my namesake's
          const dispatchLabelMine = (label) => {
            if (meRec.trainee_phase) return false; // trainees aren't dispatched
            const toks = String(label || "").replace(/\bGPM\b/gi, "").replace(/\b\d+K?\b/gi, "")
              .trim().toUpperCase().split(/\s+/).filter(Boolean);
            if (toks[0] !== LAST) return false;
            const letters = toks.slice(1).filter(t => /^[A-Z]$/.test(t));
            return letters.every(t => t === MY_INITIAL || t === "J" || t === "K");
          };
          const { data: rch } = await supabase.from("reservation_changes").select("pilot_label, changed_at");
          next.travel = (rch || []).some(c =>
            travelLabelMine(c.pilot_label) &&
            (!meRec.travel_seen_at || new Date(c.changed_at) > new Date(meRec.travel_seen_at)));
          const { data: dch } = await supabase.from("dispatch_changes").select("pilot_label, changed_at");
          next.dispatch = (dch || []).some(c =>
            dispatchLabelMine(c.pilot_label) &&
            (!meRec.dispatch_seen_at || new Date(c.changed_at) > new Date(meRec.dispatch_seen_at)));
        }
        // overdue paperwork (slips/expenses past the 24h deadline): the dot
        // clears when the item is resolved, not when the page is visited —
        // same semantics as the cargo dot. RLS scopes the count to me.
        try {
          const { count: pw } = await supabase.from("paperwork_nudge_state")
            .select("id", { count: "exact", head: true }).is("resolved_at", null);
          next.paperwork = pw || 0;
        } catch { /* table may not exist yet in a stale client — dot stays off */ }
        if (["president", "vice_president", "secretary", "member"].includes(pos)) {
          const isExec = ["president", "vice_president"].includes(pos);
          const { data: t } = await supabase.from("pilot_tickets").select("id, status, assigned_to");
          next.board = (t || []).filter(x => ["open", "assigned"].includes(x.status) &&
            (isExec || x.assigned_to === meRec?.id)).length;
        }
        // The chair is a committee position (pilot_positions) since 2026-07-19;
        // board_position holds only board seats and never names the chair.
        if ((committeePositions || []).includes("training_chair")) {
          const { data: rides } = await supabase.from("trainee_voyage_requests").select("id, status, voyage_end");
          // a voyage that sailed is archived on the CLAA page and no longer
          // waits on anyone (Jill 2026-10-23); same 2-day grace as the page
          const sailedD = new Date(`${localToday()}T12:00:00`);
          sailedD.setDate(sailedD.getDate() - 2);
          const sailed = sailedD.toISOString().slice(0, 10);
          const { count: phChair } = await supabase.from("trainee_phase_requests")
            .select("id", { count: "exact", head: true }).eq("status", "requested");
          next.chair = (rides || []).filter(x => ["requested", "sent_to_ship"].includes(x.status) && !(x.voyage_end && x.voyage_end < sailed)).length + (phChair || 0);
          try {
            const { count: endChair } = await supabase.from("pilot_endorsement_requests")
              .select("id", { count: "exact", head: true }).eq("status", "requested");
            next.chair += endChair || 0;
          } catch { /* table may not exist yet in a stale client — count stays as-is */ }
          // graded records the Chair reviews (a trainee with no mentor who
          // can be told, not a licensed deputy): no email per record since
          // Jill 2026-10-23, so they show here instead. An inactive mentor,
          // or one with no address, hands the queue to the Chair, as the
          // over-100 alert does (Codex on #614).
          try {
            // every graded record, paged by id past the 1,000-row cap (Codex on #614)
            const waiting = [];
            for (let after = ""; ;) {
              let q = supabase.from("tr_records").select("id, trainee_id").eq("status", "graded").order("id").limit(1000);
              if (after) q = q.gt("id", after);
              const { data, error } = await q;
              if (error) throw error;
              waiting.push(...(data || []));
              if (!data || data.length < 1000) break;
              after = data[data.length - 1].id;
            }
            const { data: tm } = await supabase.from("trainee_mentors").select("trainee_id, mentor_pilot_id");
            const mentorIds = [...new Set((tm || []).map((m) => m.mentor_pilot_id))];
            const { data: mp } = mentorIds.length
              ? await supabase.from("pilots_info").select("id, is_active, email").in("id", mentorIds)
              : { data: [] };
            const reachable = new Set((mp || []).filter((p) => p.is_active !== false && emailable(p.email)).map((p) => p.id));
            const mentored = new Set((tm || []).filter((m) => reachable.has(m.mentor_pilot_id)).map((m) => m.trainee_id));
            const ids = [...new Set((waiting || []).map((r) => r.trainee_id))].filter((id) => !mentored.has(id));
            if (ids.length) {
              // a licensed deputy is the Deputy Organizer's; an inactive
              // trainee is off the Chair's page (Codex on #614)
              const { data: tr } = await supabase.from("pilots_info").select("id, deputy_license_level, is_active").in("id", ids);
              const dep = new Set((tr || []).filter((d) => d.deputy_license_level || d.is_active === false).map((d) => d.id));
              next.chairReview = (waiting || []).filter((r) => ids.includes(r.trainee_id) && !dep.has(r.trainee_id)).length;
            }
          } catch { /* best effort, like every dot */ }
        }
        if (!cancelled) setDots(next);
      } catch { /* dots are best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [user, boardPosition, committeePositions, refreshTick]);

  // Visiting the page clears its dot right away (the page itself calls the
  // mark_*_seen RPC, so it stays cleared on the next load too).
  useEffect(() => {
    if (pathname === "/my-travel") setDots(d => (d.travel ? { ...d, travel: false } : d));
    if (pathname === "/dispatch-calendar") setDots(d => (d.dispatch ? { ...d, dispatch: false } : d));
    if (pathname === "/board-queue") setDots(d => (d.board ? { ...d, board: 0 } : d));
    if (pathname === "/claa-trainee-queue") setDots(d => (d.chair ? { ...d, chair: 0 } : d));
    if (pathname === "/tr-grading") setDots(d => (d.grading ? { ...d, grading: 0 } : d));
    if (pathname === "/training-records") setDots(d => (d.training ? { ...d, training: false } : d));
    if (pathname === "/mentor-review") setDots(d => (d.mentor ? { ...d, mentor: 0 } : d));
  }, [pathname]);

  return { dots, navFlags };
}

const RedDot = ({ style }) => (
  <span style={{
    width: 8, height: 8, borderRadius: "50%", background: "var(--danger-fg)",
    border: "1.5px solid white", display: "inline-block", ...style,
  }} />
);

// Sync-truth line moved to ../lib/syncSummary so the crash-recovery screen
// can show the same count (2026-07-24: a "clear website data" recovery step
// destroyed a device-only receipt no screen had warned about).

export default function TopNav({ userRoles = [], onLogout }) {
  const [syncSummary, setSyncSummary] = useState(computeSyncSummary);
  useEffect(() => {
    const update = () => setSyncSummary(computeSyncSummary());
    window.addEventListener("submit-queue-changed", update);
    window.addEventListener("online", update);
    const iv = setInterval(update, 20000);
    return () => {
      window.removeEventListener("submit-queue-changed", update);
      window.removeEventListener("online", update);
      clearInterval(iv);
    };
  }, []);
  const { user, boardPosition, committeePositions, offline } = useAuth();
  // Signing out with no internet (Jill 2026-09-24): say what it means before
  // doing it. The saved briefs stay reachable from the login page.
  const handleLogout = async () => {
    let noInternet = offline || (typeof navigator !== "undefined" && navigator.onLine === false);
    // Ship and PPU wifi report "online" with no internet behind it, and the
    // offline banner may not be up yet, so ask the server (3 s) before
    // deciding not to warn (Codex review of #506).
    if (!noInternet) {
      // A plain timer, not AbortSignal.timeout: older iPads lack it, and a
      // hung request would then hold the Logout tap forever (Codex review).
      const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
      let timer;
      try {
        const res = await Promise.race([
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/health`, {
            method: "GET",
            cache: "no-store",
            headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
            ...(ctrl ? { signal: ctrl.signal } : {}),
          }),
          new Promise((_, reject) => { timer = setTimeout(() => { ctrl?.abort(); reject(new Error("timeout")); }, 3000); }),
        ]);
        noInternet = !res.ok;
      } catch { noInternet = true; }
      finally { clearTimeout(timer); }
    }
    if (noInternet && !window.confirm(
      "You're offline. If you sign out, you can't sign back in until you have internet.\n\n" +
      "Your saved transit briefs stay on this device. Open them from the sign-in page with \"Continue offline\".\n\n" +
      "Sign out anyway?"
    )) return;
    onLogout?.();
  };
  const { pathname } = useLocation();
  const narrow = useIsNarrow();
  const { dots, navFlags } = useAttention(user, boardPosition, committeePositions);

  // Unread What's New entries — pure localStorage (announcements ship in the
  // bundle), so unlike the dots above this badge works on dead boat wifi.
  const [newsUnseen, setNewsUnseen] = useState(0);
  useEffect(() => {
    if (!user) { setNewsUnseen(0); return; }
    const update = () => setNewsUnseen(unseenCount(user.id, { roles: userRoles, boardPosition, committeePositions }));
    update();
    window.addEventListener(SEEN_EVENT, update);
    return () => window.removeEventListener(SEEN_EVENT, update);
  }, [user, userRoles, boardPosition, committeePositions]);

  const { tabs, moreGroups, homePath } = buildNav({ roles: userRoles, boardPosition, committeePositions, ...navFlags });

  const [moreOpen, setMoreOpen] = useState(false);   // More menu / sheet
  const [meOpen, setMeOpen] = useState(false);       // account menu
  const [themeMode, setThemeModeState] = useState(getThemeMode()); // 'light' | 'dark'
  const moreRef = useRef(null);
  const meRef = useRef(null);

  useEffect(() => { setMoreOpen(false); setMeOpen(false); }, [pathname]);
  useEffect(() => {
    if (!moreOpen && !meOpen) return;
    const onDoc = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (meRef.current && !meRef.current.contains(e.target)) setMeOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [moreOpen, meOpen]);

  // Tab links may carry a query (Dispatch opens on Full Dispatch for the
  // office); the tab is active on its page whatever the query says.
  const isActive = (p) => pathname === String(p).split("?")[0];
  const tabDot = (path) =>
    (path === "/my-travel" && dots.travel) || (path === "/dispatch-calendar" && dots.dispatch) ||
    (path === "/dispatch-calendar" && dots.paperwork > 0) ||
    (path === "/training-records" && dots.training);
  const itemDot = (item) =>
    (item.attention === "board" && dots.board > 0) || (item.attention === "chair" && dots.chair > 0) ||
    (item.attention === "chairReview" && dots.chairReview > 0) ||
    (item.attention === "grading" && dots.grading > 0) || (item.attention === "training" && dots.training) ||
    (item.attention === "mentor" && dots.mentor > 0) || (item.attention === "cargo" && dots.cargo > 0) ||
    (item.attention === "committee" && dots.committee > 0) ||
    (item.attention === "whatsnew" && newsUnseen > 0);
  const moreHasDot = moreGroups.some(g => g.items.some(itemDot));

  // Bottom bar fits 4 tabs + More; extra tabs (office capability logins) fold
  // into the sheet so nothing is ever unreachable.
  const barTabs = narrow ? tabs.slice(0, 4) : tabs;
  const overflowTabs = narrow ? tabs.slice(4) : [];
  const sheetGroups = overflowTabs.length
    ? [{ label: "More tools", items: overflowTabs }, ...moreGroups]
    : moreGroups;

  const initial = (user?.email || "?").charAt(0).toUpperCase();

  // Standalone dark-mode toggle, visible in the nav bar (not buried in a menu).
  // Sits on the always-dark-navy chrome bar, so it uses navFg in both themes.
  const themeToggleBtn = (
    <button
      onClick={() => setThemeModeState(toggleThemeMode())}
      aria-label={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      title={themeMode === "dark" ? "Light mode" : "Dark mode"}
      style={{
        // quiet ghost circle, thin-stroke icon — the old 36px bubble with a fat
        // 20px icon read as cartoonish (Jill 2026-07-10)
        marginLeft: "auto", width: 30, height: 30, minWidth: 30, padding: 0, borderRadius: "50%",
        border: "1px solid rgba(255,255,255,0.22)", boxSizing: "border-box",
        cursor: "pointer", background: "transparent", color: "#ffffff",
        display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none",
      }}
    >
      {themeMode === "dark" ? <Sun size={15} color="rgba(255,255,255,0.85)" strokeWidth={1.75} /> : <Moon size={15} color="rgba(255,255,255,0.85)" strokeWidth={1.75} />}
    </button>
  );

  const accountBtn = (
    <div ref={meRef} style={{ position: "relative", marginLeft: 10 }}>
      <button
        onClick={() => setMeOpen(o => !o)}
        aria-label="Account"
        style={{
          // Quiet ghost square to match the dark-mode toggle beside it — the
          // filled compass-gold circle read as a loud "mustard blob" next to
          // the restrained chrome (Jill 2026-07-13). Soft-cornered, not round.
          width: 30, height: 30, minWidth: 30, padding: 0, borderRadius: 8, boxSizing: "border-box",
          border: "1px solid rgba(255,255,255,0.22)", cursor: "pointer",
          background: "transparent", color: "rgba(255,255,255,0.9)", fontWeight: 700, fontSize: "0.85rem",
          display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none",
        }}
      >
        {initial}
      </button>
      {meOpen && (
        <div style={{
          position: "absolute", top: "100%", right: 0, marginTop: 8, background: theme.tokens.surface,
          border: `1px solid ${theme.tokens.border}`,
          borderRadius: 10, boxShadow: theme.tokens.shadowRaised, padding: 8, minWidth: 200, zIndex: 1002,
        }}>
          <div style={{ padding: "6px 12px", fontSize: "0.75rem", color: theme.tokens.faint, overflow: "hidden", textOverflow: "ellipsis" }}>{user?.email}</div>
          {(userRoles.includes("pilot") || userRoles.includes("system_admin") || userRoles.includes("trainee")) && (
            <Link to={userRoles.includes("trainee") && !userRoles.includes("pilot") ? "/profile" : "/pilot-profile"}
              style={{ display: "block", padding: "9px 12px", color: theme.tokens.textMuted, textDecoration: "none", fontWeight: 600, borderRadius: 6 }}>
              My Profile
            </Link>
          )}
          <Link to="/change-password" onClick={() => setMeOpen(false)}
            style={{ display: "block", padding: "9px 12px", color: theme.tokens.textMuted, textDecoration: "none", fontWeight: 600, borderRadius: 6 }}>
            Change password
          </Link>
          <Link to="/terms" onClick={() => setMeOpen(false)}
            style={{ display: "block", padding: "9px 12px", color: theme.tokens.textMuted, textDecoration: "none", fontWeight: 600, borderRadius: 6 }}>
            Terms &amp; Privacy
          </Link>
          <Link to="/support" onClick={() => setMeOpen(false)}
            style={{ display: "block", padding: "9px 12px", color: theme.tokens.textMuted, textDecoration: "none", fontWeight: 600, borderRadius: 6 }}>
            Portal Support
          </Link>
          <button onClick={() => { setMeOpen(false); window.dispatchEvent(new Event("seapa:open-feedback")); }} style={{
            display: "block", width: "100%", padding: "9px 12px",
            background: "transparent", border: "none", cursor: "pointer", color: theme.tokens.textMuted,
            fontWeight: 600, fontSize: "0.9rem", borderRadius: 6, textAlign: "left",
          }}>
            Send feedback
          </button>
          <button onClick={handleLogout} style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px",
            background: "transparent", border: "none", cursor: "pointer", color: theme.tokens.danger.fg,
            fontWeight: 600, fontSize: "0.9rem", borderRadius: 6, textAlign: "left",
          }}>
            <LogOut size={15} /> Logout
          </button>
          {/* the single synced-truth line (Jill 2026-07-12): is anything on
              this device still waiting to reach the account? */}
          <div style={{ padding: "6px 12px 2px", fontSize: "0.75rem", fontWeight: 600, color: syncSummary.waiting ? "var(--warn-fg)" : "var(--success-fg)" }}>
            {syncSummary.text}
          </div>
          {/* which build this device is running — ends the "did my app update?"
              guessing game (stamped from the git commit at build time) */}
          <div style={{ padding: "4px 12px 2px", fontSize: "0.68rem", color: theme.tokens.faint }}>
            version {typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev"}
          </div>
        </div>
      )}
    </div>
  );

  const groupList = (groups, onNavigate) => (
    <>
      {groups.map((g, gi) => (
        <div key={gi}>
          {g.label && (
            <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em", color: theme.tokens.faint, padding: gi === 0 ? "4px 12px 4px" : "10px 12px 4px", fontWeight: 700 }}>
              {g.label}
            </div>
          )}
          {g.items.map(item => (
            <Link key={item.path + item.label} to={item.path} onClick={onNavigate}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "9px 12px",
                color: isActive(item.path) ? theme.colors.primary : theme.tokens.ink,
                background: isActive(item.path) ? theme.tokens.info.bg : "transparent",
                textDecoration: "none", fontWeight: isActive(item.path) ? 700 : 500,
                borderRadius: 6, whiteSpace: "nowrap",
              }}>
              {item.label}
              {itemDot(item) && <RedDot />}
              {item.attention === "board" && dots.board > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{dots.board}</span>}
              {item.attention === "chair" && dots.chair > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{dots.chair}</span>}
              {item.attention === "chairReview" && dots.chairReview > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{dots.chairReview}</span>}
              {item.attention === "grading" && dots.grading > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{dots.grading}</span>}
              {item.attention === "mentor" && dots.mentor > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{dots.mentor}</span>}
              {item.attention === "committee" && dots.committee > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{dots.committee}</span>}
              {item.attention === "whatsnew" && newsUnseen > 0 && <span style={{ fontSize: "0.75rem", color: "var(--danger-fg)", fontWeight: 700 }}>{newsUnseen}</span>}
            </Link>
          ))}
        </div>
      ))}
    </>
  );

  // ---------- phone / iPad: slim top bar + bottom tab bar ----------
  if (narrow) {
    return (
      <>
        <header style={{
          display: "flex", alignItems: "center", gap: 10, backgroundColor: theme.tokens.navBg,
          color: theme.tokens.navFg, padding: "8px 14px", boxShadow: theme.shadows.navbar, position: "relative", zIndex: 1000,
        }}>
          <Link to={homePath} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <img src={seapaLogo} alt="SEAPA Logo" style={{ height: 32 }} />
            <span style={{ color: theme.tokens.navFg, fontWeight: 600, fontSize: "1rem", whiteSpace: "nowrap" }}>SEAPA Portal</span>
          </Link>
          {themeToggleBtn}
          {accountBtn}
        </header>

        {moreOpen && (
          <div onClick={() => setMoreOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.35)", zIndex: 1001 }} />
        )}
        {moreOpen && (
          <div style={{
            position: "fixed", left: 10, right: 10, bottom: "calc(66px + env(safe-area-inset-bottom))",
            background: theme.tokens.surface, border: `1px solid ${theme.tokens.border}`, borderRadius: 14, boxShadow: "0 -4px 24px rgba(0,0,0,0.18)",
            padding: 10, maxHeight: "60vh", overflowY: "auto", zIndex: 1002,
          }}>
            {groupList(sheetGroups, () => setMoreOpen(false))}
            {/* Second home for feedback on phones: the account-menu copy is in
                the header, which had scrolled off Paul's screen when he went
                looking for "report a problem" (2026-09-06). The tab bar is
                always on screen, so this one cannot go missing. */}
            <div style={{ borderTop: `1px solid ${theme.tokens.border}`, marginTop: 8, paddingTop: 6 }}>
              <button
                onClick={() => { setMoreOpen(false); window.dispatchEvent(new Event("seapa:open-feedback")); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px",
                  background: "transparent", border: "none", cursor: "pointer", color: theme.tokens.ink,
                  fontWeight: 500, fontSize: "1rem", borderRadius: 6, textAlign: "left",
                }}
              >
                Send feedback
              </button>
            </div>
          </div>
        )}

        {/* Not position:fixed — the bar is a flow child of the .app-shell flex
            column (kit.css), placed under the scrolling <main> by order:99.
            iOS Safari can't detach it mid-scroll because nothing is fixed and
            the body never scrolls. */}
        <nav className="bottom-tab-bar" style={{
          display: "grid", gridTemplateColumns: `repeat(${barTabs.length + 1}, 1fr)`,
          background: theme.tokens.surface, borderTop: `1px solid ${theme.tokens.border}`,
          padding: "6px 2px calc(8px + env(safe-area-inset-bottom))",
        }}>
          {barTabs.map(t => {
            const Icon = ICONS[t.icon] || CalendarDays;
            const active = isActive(t.path);
            return (
              <Link key={t.path} to={t.path} style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                textDecoration: "none", color: active ? theme.colors.primary : theme.tokens.faint,
                fontSize: "0.62rem", fontWeight: active ? 700 : 600, position: "relative",
              }}>
                {tabDot(t.path) && <RedDot style={{ position: "absolute", top: -2, right: "calc(50% - 16px)" }} />}
                <Icon size={22} strokeWidth={active ? 2.4 : 2} />
                {t.label}
              </Link>
            );
          })}
          <button onClick={() => setMoreOpen(o => !o)} style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            background: "transparent", border: "none", cursor: "pointer", position: "relative",
            color: moreOpen ? theme.colors.primary : theme.tokens.faint, fontSize: "0.62rem", fontWeight: 600, padding: 0,
          }}>
            {moreHasDot && <RedDot style={{ position: "absolute", top: -2, right: "calc(50% - 16px)" }} />}
            <MoreHorizontal size={22} />
            More
          </button>
        </nav>
      </>
    );
  }

  // ---------- desktop: top bar ----------
  return (
    <header style={{
      display: "flex", alignItems: "center", gap: 18, backgroundColor: theme.tokens.navBg,
      color: theme.tokens.navFg, padding: "8px 18px", boxShadow: theme.shadows.navbar, position: "relative", zIndex: 1000,
    }}>
      <Link to={homePath} style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <img src={seapaLogo} alt="SEAPA Logo" style={{ height: 36 }} />
        <span style={{ color: theme.tokens.navFg, fontWeight: 600, fontSize: "1.05rem", whiteSpace: "nowrap" }}>SEAPA Portal</span>
      </Link>

      <nav style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        {tabs.map(t => {
          const Icon = ICONS[t.icon] || CalendarDays;
          const active = isActive(t.path);
          return (
            <Link key={t.path} to={t.path} style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px",
              borderRadius: 8, textDecoration: "none", whiteSpace: "nowrap",
              background: active ? "rgba(255,255,255,0.16)" : "transparent",
              color: active ? "white" : "rgba(255,255,255,0.85)",
              fontWeight: active ? 700 : 600, fontSize: "0.9rem", position: "relative",
            }}>
              <Icon size={16} />
              {t.label}
              {tabDot(t.path) && <RedDot style={{ position: "absolute", top: 4, right: 4 }} />}
            </Link>
          );
        })}

        {moreGroups.length > 0 && (
          <div ref={moreRef} style={{ position: "relative" }}>
            <button onClick={() => setMoreOpen(o => !o)} style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px",
              borderRadius: 8, border: "none", cursor: "pointer", position: "relative",
              background: moreOpen ? "rgba(255,255,255,0.16)" : "transparent",
              color: "rgba(255,255,255,0.9)", fontWeight: 600, fontSize: "0.9rem",
            }}>
              <MoreHorizontal size={16} />
              More
              {moreHasDot && <RedDot style={{ position: "absolute", top: 4, right: 4 }} />}
            </button>
            {moreOpen && (
              <div style={{
                position: "absolute", top: "100%", left: 0, marginTop: 8, background: theme.tokens.surface,
                border: `1px solid ${theme.tokens.border}`,
                borderRadius: 10, boxShadow: theme.tokens.shadowRaised, padding: 8, minWidth: 250, zIndex: 1001,
                maxHeight: "70vh", overflowY: "auto",
              }}>
                {groupList(moreGroups)}
              </div>
            )}
          </div>
        )}
      </nav>

      {themeToggleBtn}
      {accountBtn}
    </header>
  );
}
